// ═══ 평가 정산 페이지 — EvalAdminPage.js에서 분리 ═══
// 월별 검차원 정산: 검차 건수/기본료/등급 보너스 합계 + 정산 처리
import React, { useState, useEffect } from 'react';
import { authFetch } from '../utils/api';
import CustomModal from './CustomModal';

function SettlementPage({ BASE, onBack }) {
  var now = new Date();
  var [year, setYear] = useState(now.getFullYear());
  var [month, setMonth] = useState(now.getMonth() + 1);
  var [settlements, setSettlements] = useState([]);
  // R4 (2026-08-31) — generate dry-run(preview) 결과. 배지/재집계 요약 전용, DB 미변경.
  var [preview, setPreview] = useState([]);
  var [selectedId, setSelectedId] = useState(null);
  var [detail, setDetail] = useState(null);
  var [loading, setLoading] = useState(false);
  // Wave S3 (2026-05-21) — 글로벌 mutation 로딩 상태 (generate / settle). 중복 클릭 차단.
  var [submitting, setSubmitting] = useState(false);
  // Wave S1 (2026-05-21) — 통합 에러/성공 모달. silent catch + native alert 일괄 전환.
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  var showError = function(err, defaultMsg) {
    var msg = (err && err.response && err.response.data && err.response.data.error) ||
              (err && err.message) ||
              (typeof err === 'string' ? err : null) ||
              defaultMsg || '오류가 발생했습니다';
    setModal({ type: 'danger', title: '실패', message: msg, hideCancel: true, onConfirm: closeModal });
  };

  // ═══ R4 (2026-08-31) — 집계 신선도 ═══
  // 실사고: 평가사가 "7월에 10건 넘게 했는데 정산엔 5건" 신고. 정산은 관리자가 눌러야 찍히는
  //   스냅샷인데 화면에 "이 숫자가 언제 집계된 것인지" 표기가 전혀 없어 오해가 증폭됐다.
  //   서버는 created_at(최초 집계) / updated_at(재집계) 를 이미 내려준다 (routes SELECT s.*).
  // updated_at 우선, 없으면 created_at. 둘 다 없으면 표기 생략 — 없는 값을 만들어내지 않는다.
  var pickAggAt = function(row) { return (row && (row.updated_at || row.created_at)) || ''; };
  // 'YYYY-MM-DD HH:mm:ss'(sqlite) 와 ISO Z(mysql2 Date → JSON) 두 형태 모두 수용.
  //   ISO 는 +9h 후 UTC 필드로 KST wall clock 복원 — utils/pdfTemplates.js fmtDate10 과 동일 규약.
  var fmtStamp = function(v) {
    if (!v) return '';
    var str = String(v);
    if (str.indexOf('T') > -1) {
      var d = new Date(str);
      if (isNaN(d.getTime())) return '';
      var k = new Date(d.getTime() + 9 * 3600 * 1000);
      return (k.getUTCMonth() + 1) + '/' + k.getUTCDate() + ' ' + String(k.getUTCHours()).padStart(2, '0') + ':' + String(k.getUTCMinutes()).padStart(2, '0');
    }
    var m = str.match(/^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2})/);
    if (!m) return '';
    return Number(m[2]) + '/' + Number(m[3]) + ' ' + m[4] + ':' + m[5];
  };
  var stampMs = function(v) {
    if (!v) return 0;
    var t = new Date(String(v).replace(' ', 'T')).getTime();
    return isNaN(t) ? 0 : t;
  };
  // preview 응답 매칭 키는 평가사 이름 — 서버 계약이 { inspector, level, jobs, total, action, before } 로
  //   id 를 주지 않는다.
  var previewFor = function(name) {
    for (var i = 0; i < preview.length; i++) { if (preview[i].inspector === name) return preview[i]; }
    return null;
  };
  var badgeFor = function(name) {
    var p = previewFor(name);
    if (!p) return '';
    if (p.action === 'delete') return '정리 예정';
    if (p.action === 'update' && p.before && p.jobs !== p.before.jobs) {
      var diff = p.jobs - p.before.jobs;
      return diff > 0 ? '미반영 ' + diff + '건' : '감소 ' + Math.abs(diff) + '건';
    }
    return '';
  };
  // 재집계 예상 요약 — 상단 배너 + '정산 생성' 확인 모달 공용.
  var previewNotes = function() {
    var unreflected = 0, creates = 0, createJobs = 0, deletes = 0;
    for (var i = 0; i < preview.length; i++) {
      var p = preview[i];
      if (p.action === 'create') { creates += 1; createJobs += (p.jobs || 0); }
      else if (p.action === 'delete') { deletes += 1; }
      else if (p.action === 'update' && p.before && p.jobs !== p.before.jobs) { unreflected += (p.jobs - p.before.jobs); }
    }
    var parts = [];
    if (unreflected > 0) parts.push('미반영 ' + unreflected + '건');
    if (unreflected < 0) parts.push('감소 ' + Math.abs(unreflected) + '건');
    if (creates > 0) parts.push('신규 평가사 ' + creates + '명 (' + createJobs + '건)');
    if (deletes > 0) parts.push('정리 예정 ' + deletes + '건');
    return parts;
  };

  var fetchSettlements = function() {
    authFetch(BASE + '/api/eval/settlements?year=' + year + '&month=' + month).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setSettlements(d.data || []);
    }).catch(function(e) { showError(e); });
  };

  // 상세 로딩
  useEffect(function() {
    if (!selectedId) { setDetail(null); return; }
    authFetch(BASE + '/api/eval/settlements/' + selectedId).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setDetail(d.data);
      else { setDetail(null); setSelectedId(null); }
    }).catch(function(e) { setDetail(null); setSelectedId(null); showError(e); });
  }, [selectedId]);

  // preview(dry-run) — DB 미변경이라 월 진입 시 안전하게 호출. 실패해도 화면은 정상 동작(배지만 생략).
  var fetchPreview = function() {
    authFetch(BASE + '/api/eval/settlements/generate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ year: year, month: month, preview: true }),
    }).then(function(r) { return r.json(); }).then(function(d) {
      setPreview((d && d.success && d.preview && Array.isArray(d.data)) ? d.data : []);
    }).catch(function() { setPreview([]); });
  };

  useEffect(function() { fetchSettlements(); fetchPreview(); }, [year, month]);

  // R4 (2026-08-31) — '정산 생성' 확인 모달. handleSettle(Wave S2) 패턴 복제.
  //   기존엔 확인 없이 즉시 POST — 재집계는 미정산 스냅샷을 지금 값으로 덮어쓴다.
  var handleGenerate = function() {
    var parts = previewNotes();
    var summary = parts.length
      ? '예상 변경: ' + parts.join(' · ')
      : '변경 예상 내역을 불러오지 못했습니다 (집계 후 값이 달라질 수 있습니다).';
    setModal({
      type: 'confirm',
      title: '정산 생성',
      message: year + '년 ' + month + '월 정산을 다시 집계합니다.\n' + summary + '\n미정산 스냅샷은 지금 값으로 갱신되고, 정산완료 건은 변경되지 않습니다.\n계속하시겠습니까?',
      confirmText: '정산 생성',
      onConfirm: function() {
        closeModal();
        doGenerate();
      },
    });
  };
  var doGenerate = function() {
    setLoading(true);
    setSubmitting(true);
    authFetch(BASE + '/api/eval/settlements/generate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ year: year, month: month }),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d && d.success) {
        var n = (d.data || []).length;
        var total = (d.data || []).reduce(function(s, x) { return s + (x.total || 0); }, 0);
        var msg = '정산 ' + n + '건 ' + (n > 0 ? '생성/갱신 완료 (합계 ' + total.toLocaleString() + '원)' : '대상 없음 — 해당 월에 완료된 검차가 없습니다.');
        setModal({ type: 'success', title: '정산 생성', message: msg, onConfirm: closeModal });
        fetchSettlements();
        fetchPreview();
      } else {
        showError(d && d.error, '정산 생성 실패');
      }
    }).catch(function(err) {
      showError(err, '정산 생성 네트워크 오류');
    }).finally(function() { setLoading(false); setSubmitting(false); });
  };

  // Wave S2 (2026-05-21) — 정산 완료 처리 전 확인 다이얼로그. 되돌릴 수 없는 결정.
  var handleSettle = function(id) {
    setModal({
      type: 'confirm',
      title: '정산 완료 처리',
      message: '이 정산을 완료 처리합니다. 처리 후 status가 settled로 영구 변경되며 되돌릴 수 없습니다.\n계속하시겠습니까?',
      confirmText: '정산 완료',
      onConfirm: function() {
        closeModal();
        doSettle(id);
      },
    });
  };
  var doSettle = function(id) {
    setSubmitting(true);
    authFetch(BASE + '/api/eval/settlements/' + id + '/settle', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }).then(function() { fetchSettlements(); setSelectedId(null); })
      .catch(function(e) { showError(e, '정산 완료 처리 실패'); })
      .finally(function() { setSubmitting(false); });
  };

  // Wave S4 (2026-05-21) — 정산서 PDF 다운로드. 토큰 발급 후 <a href> native 다운로드.
  //   <a href> 는 Authorization 헤더 못 실음 → 짧은 TTL 서명 토큰 + ?token= 쿼리.
  var handleDownloadPdf = function(id) {
    setSubmitting(true);
    authFetch(BASE + '/api/eval/settlements/' + id + '/download-token', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d && d.success && d.token) {
        var url = BASE + '/api/eval/settlements/' + id + '/pdf?token=' + encodeURIComponent(d.token);
        var a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        showError(d && d.error, 'PDF 다운로드 토큰 발급 실패');
      }
    }).catch(function(e) {
      showError(e, 'PDF 다운로드 토큰 발급 실패');
    }).finally(function() { setSubmitting(false); });
  };

  // Wave S3 (2026-05-21) — 글로벌 mutation 오버레이. submitting 시 클릭 차단 + 시각 피드백.
  var overlay = submitting ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(15,23,42,0.45)', zIndex: 9998,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      pointerEvents: 'auto',
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: '20px 32px',
        display: 'flex', alignItems: 'center', gap: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      }}>
        <div className="spinner" style={{
          width: 22, height: 22, borderRadius: '50%',
          border: '3px solid #e5e7eb', borderTopColor: '#1B3A5C',
          animation: 'spin 0.8s linear infinite',
        }}/>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1B3A5C' }}>처리 중...</span>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  ) : null;

  // 상세 로딩 중
  if (selectedId && !detail) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px' }}>
        {overlay}
        <button onClick={function() { setSelectedId(null); }} style={{ background: 'none', border: 'none', fontSize: 15, color: '#3B8BD4', padding: 0, marginBottom: 14, fontWeight: 600, cursor: 'pointer' }}>← 정산 목록</button>
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa', fontSize: 14 }}>로딩 중...</div>
        {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
      </div>
    );
  }

  // 상세 보기
  if (selectedId && detail) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px' }}>
        {overlay}
        <button onClick={function() { setSelectedId(null); setDetail(null); }} style={{ background: 'none', border: 'none', fontSize: 15, color: '#3B8BD4', padding: 0, marginBottom: 14, fontWeight: 600, cursor: 'pointer' }}>← 정산 목록</button>
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', padding: 18, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#111' }}>{detail.inspector_name}</span>
              <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, color: '#534AB7', background: '#EEEDFE' }}>{detail.inspector_level}</span>
            </div>
            <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: detail.status === 'settled' ? '#1D9E75' : '#D85A30', background: detail.status === 'settled' ? '#E1F5EE' : '#FAECE7' }}>{detail.status === 'settled' ? '정산완료' : '미정산'}</span>
          </div>
          {fmtStamp(pickAggAt(detail)) && (
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>최종 집계: {fmtStamp(pickAggAt(detail))}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div style={{ background: '#f8f8f5', borderRadius: 8, padding: '8px 0', textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 700 }}>{detail.total_jobs}</div><div style={{ fontSize: 10, color: '#aaa' }}>건수</div></div>
            <div style={{ background: '#f8f8f5', borderRadius: 8, padding: '8px 0', textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 700, color: '#534AB7' }}>{(detail.level_bonus || 0).toLocaleString()}</div><div style={{ fontSize: 10, color: '#aaa' }}>등급보너스</div></div>
            <div style={{ background: '#f8f8f5', borderRadius: 8, padding: '8px 0', textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 700, color: '#D85A30' }}>{(detail.total_amount || 0).toLocaleString()}</div><div style={{ fontSize: 10, color: '#aaa' }}>합계</div></div>
          </div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#222' }}>검차 내역</div>
        {(detail.items || []).map(function(it, i) {
          return (
            <div key={i} style={{ background: '#fff', borderRadius: 10, border: '1px solid #eee', padding: '10px 14px', marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div><span style={{ fontSize: 13, fontWeight: 600 }}>{it.car_model}</span> <span style={{ fontSize: 11, color: '#aaa' }}>{it.car_number}</span></div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#222' }}>{(it.total_fee || 0).toLocaleString()}원</span>
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{it.inspect_date} · {it.order_no} · 기본 {(it.base_fee || 0).toLocaleString()} + 보너스 {(it.level_bonus || 0).toLocaleString()}</div>
            </div>
          );
        })}
        {detail.status !== 'settled' && (
          <button onClick={function() { handleSettle(detail.id); }} style={{ width: '100%', marginTop: 12, padding: '14px 0', borderRadius: 12, border: 'none', background: '#1D9E75', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>정산 완료 처리</button>
        )}
        {/* Wave S4 (2026-05-21) — 정산서 PDF 다운로드 (상태 무관). 평가사 본인 전달용. */}
        <button onClick={function() { handleDownloadPdf(detail.id); }} disabled={submitting} style={{ width: '100%', marginTop: 8, padding: '14px 0', borderRadius: 12, border: '1.5px solid #1B3A5C', background: '#fff', color: '#1B3A5C', fontSize: 14, fontWeight: 700, cursor: submitting ? 'default' : 'pointer' }}>📄 정산서 PDF 다운로드</button>
        {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
      </div>
    );
  }

  var totalAmount = settlements.reduce(function(s, x) { return s + (x.total_amount || 0); }, 0);
  var totalJobs = settlements.reduce(function(s, x) { return s + (x.total_jobs || 0); }, 0);
  var lastAggAt = settlements.reduce(function(acc, x) {
    var v = pickAggAt(x);
    return stampMs(v) > stampMs(acc) ? v : acc;
  }, '');
  var previewParts = previewNotes();

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px' }}>
      {overlay}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 15, color: '#3B8BD4', padding: 0, fontWeight: 600, cursor: 'pointer' }}>←</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111' }}>평가사 정산</h2>
        </div>
        <button onClick={handleGenerate} disabled={loading} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: loading ? '#aaa' : '#534AB7', color: '#fff', cursor: loading ? 'default' : 'pointer', fontSize: 13, fontWeight: 700 }}>{loading ? '생성중...' : '정산 생성'}</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <select value={year} onChange={function(e) { setYear(Number(e.target.value)); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e0dfda', fontSize: 14 }}>
          <option value={2025}>2025</option><option value={2026}>2026</option><option value={2027}>2027</option>
        </select>
        <select value={month} onChange={function(e) { setMonth(Number(e.target.value)); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e0dfda', fontSize: 14 }}>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(function(m) { return <option key={m} value={m}>{m}월</option>; })}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
        <div style={{ background: '#f8f8f5', borderRadius: 10, padding: '10px 0', textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800 }}>{settlements.length}</div><div style={{ fontSize: 10, color: '#aaa' }}>평가사</div></div>
        <div style={{ background: '#f8f8f5', borderRadius: 10, padding: '10px 0', textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800 }}>{totalJobs}</div><div style={{ fontSize: 10, color: '#aaa' }}>총 건수</div></div>
        <div style={{ background: '#f8f8f5', borderRadius: 10, padding: '10px 0', textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: '#D85A30' }}>{totalAmount.toLocaleString()}</div><div style={{ fontSize: 10, color: '#aaa' }}>총 금액</div></div>
      </div>

      {(fmtStamp(lastAggAt) || previewParts.length > 0) && (
        <div style={{ marginBottom: 12 }}>
          {fmtStamp(lastAggAt) && <div style={{ fontSize: 12, color: '#888', marginBottom: previewParts.length > 0 ? 6 : 0 }}>최종 집계: {fmtStamp(lastAggAt)}</div>}
          {previewParts.length > 0 && (
            <div style={{ background: '#FEF6E7', border: '1px solid #F5D9A6', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#8A5A0B' }}>
              <b>재집계 필요</b> — {previewParts.join(' · ')}
              <div style={{ fontSize: 11, marginTop: 4, color: '#A9761E' }}>"정산 생성"을 눌러야 반영됩니다.</div>
            </div>
          )}
        </div>
      )}

      {settlements.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>정산 데이터가 없습니다. "정산 생성"을 눌러주세요.</div>}

      {settlements.map(function(s) {
        var badge = badgeFor(s.inspector_name);
        return (
          <div key={s.id} onClick={function() { setSelectedId(s.id); }} style={{ background: '#fff', borderRadius: 12, border: '1px solid #eee', padding: '14px 16px', marginBottom: 8, cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>{s.inspector_name}</span>
                <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600, color: '#534AB7', background: '#EEEDFE' }}>{s.inspector_level}</span>
                {badge && <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, color: '#8A5A0B', background: '#FEF0D5' }}>{badge}</span>}
              </div>
              <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, color: s.status === 'settled' ? '#1D9E75' : '#D85A30', background: s.status === 'settled' ? '#E1F5EE' : '#FAECE7' }}>{s.status === 'settled' ? '정산완료' : '미정산'}</span>
            </div>
            <div style={{ fontSize: 12, color: '#888', display: 'flex', gap: 12 }}>
              <span>{s.total_jobs}건</span>
              <span>기본 {(s.base_fee || 0).toLocaleString()}</span>
              <span>보너스 {(s.level_bonus || 0).toLocaleString()}</span>
              <span style={{ fontWeight: 700, color: '#D85A30' }}>합계 {(s.total_amount || 0).toLocaleString()}원</span>
            </div>
          </div>
        );
      })}
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>
  );
}

export default SettlementPage;
